import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { BarChart3, Filter, Download, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import OrphanRigheDialog from "@/components/OrphanRigheDialog";

const fmtIt = (iso) =>
  iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "";

export default function RiepilogoPage() {
  const { user } = useAuth();
  const { t } = useTranslation("app");
  const [commesse, setCommesse] = useState([]);
  const [filterCommessa, setFilterCommessa] = useState("__all__");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ per_commessa: [], righe: [] });
  const [loading, setLoading] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);
  const [showOrphanDialog, setShowOrphanDialog] = useState(false);
  // Iter 68: info ultima esportazione gestionale (solo admin, flag utente)
  const [lastExport, setLastExport] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin" || user?.show_last_export === false) return;
    api
      .get("/rapportini/export/gestionale-last")
      .then((r) => setLastExport(r.data || null))
      .catch(() => {});
  }, [user]);

  const refreshOrphanCount = () => {
    if (user?.role !== "admin") return;
    api
      .get("/rapportini/orphan-righe/count")
      .then((r) => setOrphanCount(r.data?.orphan_righe_count || 0))
      .catch((err) => console.warn("[orphans] count failed:", err?.message));
  };

  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data || []));
    refreshOrphanCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const cleanupOrphans = () => {
    setShowOrphanDialog(true);
  };

  const exportGestionale = async () => {
    if (!from || !to) {
      toast.warning("Imposta prima un intervallo di date");
      return;
    }
    try {
      // Iter 31kk: anteprima — cosa è già stato esportato e cosa è nuovo
      const { data: prev } = await api.get(
        "/rapportini/export/gestionale-preview",
        { params: { date_from: from, date_to: to } }
      );
      const nuovi = prev.nuovi || {};
      const vecchi = prev.gia_esportati || {};
      let onlyNew = false;
      if ((vecchi.n_rapportini || 0) > 0) {
        if ((nuovi.n_rapportini || 0) === 0) {
          const again = window.confirm(
            `✅ Tutto già esportato per questo intervallo:\n` +
              `${vecchi.n_rapportini} rapportini (${vecchi.ore_tot}h) — nessuna ora nuova.\n\n` +
              `Vuoi RIESPORTARE comunque tutto?\nFile: ${prev.next_filename}`
          );
          if (!again) return;
        } else {
          const commesseNuove = (nuovi.per_commessa || [])
            .map((c) => `${c.codice} (${c.ore}h)`)
            .join(", ");
          const ok = window.confirm(
            `⚠️ Alcune ore di questo intervallo sono GIÀ state esportate al gestionale:\n` +
              `• Già esportati: ${vecchi.n_rapportini} rapportini (${vecchi.ore_tot}h) — n° ${(vecchi.numeri || []).join(", ")}\n\n` +
              `• NUOVI da esportare: ${nuovi.n_rapportini} rapportini (${nuovi.ore_tot}h) — n° ${(nuovi.numeri || []).join(", ")}\n` +
              `  Commesse: ${commesseNuove}\n\n` +
              `Esporto SOLO i nuovi nel file: ${prev.next_filename}\nConfermi?`
          );
          if (!ok) return;
          onlyNew = true;
        }
      }
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("rapportini_token");
      const url = `${apiBase}/api/rapportini/export/gestionale-excel?date_from=${from}&date_to=${to}&only_new=${onlyNew}`;
      const r = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Export fallito (HTTP ${r.status})`);
        return;
      }
      const blob = await r.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = prev.next_filename || `export-gestionale-${from}_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(
        onlyNew
          ? `Esportati SOLO i nuovi: ${nuovi.n_rapportini} rapportini (${nuovi.ore_tot}h) → ${prev.next_filename}`
          : `Excel scaricato → ${prev.next_filename}`
      );
    } catch (e) {
      console.error("[export] gestionale:", e);
      toast.error("Errore durante l'export");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCommessa && filterCommessa !== "__all__")
        params.commessa_codice = filterCommessa;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get("/rapportini/totals/by-commessa", {
        params,
      });
      setData(data);
    } catch {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filterCommessa, from, to]);

  const totaleGenerale = useMemo(
    () => data.per_commessa.reduce((s, c) => s + (c.ore_tot || 0), 0),
    [data]
  );

  const exportCsv = () => {
    const rows = [
      ["Commessa", "Dipendente", "Guida", "Passegg.", "Lavoro", "Totale", "Giorni"],
      ...data.righe.map((r) => [
        r.commessa_codice || "",
        r.employee_nome || "",
        Number(r.ore_guida || 0).toFixed(2),
        Number(r.ore_passegg || 0).toFixed(2),
        Number(r.ore_lavoro || 0).toFixed(2),
        Number(r.ore_tot || 0).toFixed(2),
        r.giorni_lavorati || 0,
      ]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `riepilogo_ore_${from || "tutto"}_${to || "tutto"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-end justify-between flex-col md:flex-row gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {t("riep.kicker")}
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            {t("riep.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t("riep.sub")}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportCsv}
          data-testid="export-csv-button"
          className="h-12"
        >
          <Download className="h-4 w-4 mr-2" /> {t("riep.exportCsv")}
        </Button>
      </div>

      {user?.role === "admin" && orphanCount > 0 && (
        <Card
          className="p-4 md:p-5 border-orange-300 bg-orange-50"
          data-testid="orphan-banner"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-md bg-orange-200 text-orange-800 grid place-items-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-base">
                Ci sono {orphanCount} righe orfane nei rapportini
              </div>
              <p className="text-sm text-slate-700 mt-0.5">
                Sono righe con una commessa cancellata o mai esistita. Apri
                l'anteprima per <b>tenere</b>, <b>riassegnare</b> a una
                commessa attiva oppure <b>eliminare</b> ognuna singolarmente.
                Le modifiche si applicano subito a stampe e report.
              </p>
            </div>
            <Button
              onClick={cleanupOrphans}
              className="h-11 bg-orange-600 hover:bg-orange-700"
              data-testid="cleanup-orphans-button"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Esamina e correggi
            </Button>
          </div>
        </Card>
      )}

      <OrphanRigheDialog
        open={showOrphanDialog}
        onClose={() => setShowOrphanDialog(false)}
        onApplied={() => {
          // Aggiorna conteggio e riepilogo per riflettere le modifiche
          refreshOrphanCount();
          load();
        }}
      />

      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">
              <Filter className="inline h-3 w-3 mr-1" /> {t("rapp.commessa")}
            </Label>
            <Select value={filterCommessa} onValueChange={setFilterCommessa}>
              <SelectTrigger
                className="h-12 mt-1"
                data-testid="riepilogo-commessa-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("riep.tutte")}</SelectItem>
                {commesse.map((c) => (
                  <SelectItem key={c.id} value={c.codice}>
                    {c.codice}
                    {c.descrizione ? ` · ${c.descrizione}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">
              {t("riep.dal")}
            </Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-12 mt-1"
              data-testid="riepilogo-from-input"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">
              {t("riep.al")}
            </Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-12 mt-1"
              data-testid="riepilogo-to-input"
            />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t("riep.stats.commesse")} value={data.per_commessa.length} />
        <Stat label={t("riep.stats.dipendenti")} value={new Set(data.righe.map((r) => r.employee_nome)).size} />
        <Stat label={t("riep.stats.oreTotali")} value={totaleGenerale.toFixed(1)} />
        <Stat label={t("riep.stats.voci")} value={data.righe.length} />
      </div>

      {user?.role === "admin" && user?.show_last_export !== false && (
        <Card
          className="p-4 bg-sky-50 border-sky-200 flex items-center gap-3"
          data-testid="last-export-card"
        >
          <div className="h-10 w-10 shrink-0 rounded-md bg-sky-200 text-sky-800 grid place-items-center">
            <Download className="h-5 w-5" />
          </div>
          <div className="text-sm text-sky-900">
            <span className="font-heading font-bold">{t("riep.exp.lastLabel")}</span>
            {lastExport?.last_export_date ? (
              <>
                : {t("riep.exp.lastCovers")}{" "}
                <b data-testid="last-export-date">{fmtIt(lastExport.last_export_date)}</b>
                {lastExport.last_export_at && (
                  <span className="text-sky-700">
                    {" "}· {t("riep.exp.lastDone")} {fmtIt(lastExport.last_export_at.slice(0, 10))}
                    {lastExport.by ? ` (${lastExport.by})` : ""}
                  </span>
                )}
              </>
            ) : (
              <>: {t("riep.exp.lastNone")}</>
            )}
          </div>
        </Card>
      )}

      {user?.role === "admin" && user?.can_export_gestionale !== false && (
        <Card className="p-4 bg-emerald-50 border-emerald-300 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="font-heading font-bold text-emerald-900">
              {t("riep.exp.title")}
            </div>
            <div className="text-sm text-emerald-800">
              {t("riep.exp.desc1")}<b>{to || "—"}</b>{t("riep.exp.desc2")}
            </div>
          </div>
          <Button
            onClick={exportGestionale}
            disabled={!from || !to}
            className="h-11 bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-gestionale-button"
          >
            <Download className="h-4 w-4 mr-2" />
            {t("riep.exp.btn")}
          </Button>
        </Card>
      )}

      {loading ? (
        <div className="p-10 text-center text-slate-500">{t("riep.loading")}</div>
      ) : data.per_commessa.length === 0 ? (
        <Card className="p-10 text-center">
          <BarChart3 className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading font-bold text-lg mt-3">
            {t("riep.noData")}
          </div>
        </Card>
      ) : (
        <div className="space-y-5">
          {data.per_commessa.map((c) => (
            <Card key={c.commessa_codice} className="p-0 overflow-hidden">
              <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-300 flex items-center gap-2">
                    {t("rapp.commessa")}
                    {(c.commessa_codice || "").toLowerCase().endsWith("-off") ? (
                      <span className="bg-amber-500 text-white px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider">
                        {t("riep.officina")}
                      </span>
                    ) : (
                      <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider">
                        {t("riep.cantiere")}
                      </span>
                    )}
                  </div>
                  <div className="font-heading font-black text-xl tracking-tight font-mono">
                    {c.commessa_codice || "—"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-300">
                    {t("riep.stats.oreTotali")}
                  </div>
                  <div className="font-heading font-black text-2xl">
                    {Number(c.ore_tot || 0).toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 font-bold">{t("riep.tab.dipendente")}</th>
                      <th className="text-right p-3 font-bold">{t("rapp.guida")}</th>
                      <th className="text-right p-3 font-bold">{t("rapp.passegg")}</th>
                      <th className="text-right p-3 font-bold">{t("rapp.lavoro")}</th>
                      <th className="text-right p-3 font-bold">{t("riep.tab.totale")}</th>
                      <th className="text-right p-3 font-bold">{t("riep.tab.giorni")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.righe.map((r, idx) => (
                      <tr
                        key={`${r.employee_id || r.employee_nome || "x"}-${idx}`}
                        className="border-t border-slate-200"
                        data-testid={`riepilogo-row-${c.commessa_codice}-${idx}`}
                      >
                        <td className="p-3 font-semibold">
                          {r.employee_nome || "—"}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {Number(r.ore_guida || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {Number(r.ore_passegg || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {Number(r.ore_lavoro || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-right font-mono font-bold">
                          {Number(r.ore_tot || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {r.giorni_lavorati || 0}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-900 bg-slate-50">
                      <td className="p-3 font-bold">{t("riep.tab.totCommessa")}</td>
                      <td className="p-3 text-right font-mono font-bold">
                        {Number(c.ore_guida || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-mono font-bold">
                        {Number(c.ore_passegg || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-mono font-bold">
                        {Number(c.ore_lavoro || 0).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-mono font-bold">
                        {Number(c.ore_tot || 0).toFixed(2)}
                      </td>
                      <td className="p-3"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="font-heading font-black text-3xl tracking-tight mt-1">
        {value}
      </div>
    </Card>
  );
}
